/**
 * Command for transforming entities (move, rotate, scale).
 * Supports undo/redo by storing the transform delta.
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { findEntityByHandle } from "../core/oda";
import { counterRotatePhotoMarkerText } from "../photomarker";
import { HatchReactorManager } from "../managers/HatchReactorManager";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";
import {
  applyTransformToEntity,
  type EntityTransformParams,
} from "../transform/entityTransform";

/**
 * Command to transform entities with undo/redo support.
 */
export class TransformEntityCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private module: DrawingModuleType;
  private params: EntityTransformParams[];
  private executed = false;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: EntityTransformParams[]) {
    this.viewer = viewer;
    this.module = viewer.module;
    this.params = params;
  }

  /**
   * Execute the transform (transforms are already applied by handle).
   */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(
      this.viewer,
      this.params.map((p) => ({ handle: p.handle, changeKind: "moved" })),
    );

    if (this.executed) return;

    // Transforms are already applied by the handle during interaction
    // This command just tracks state for undo/redo
    this.executed = true;

    // Sync hatches for all transformed entities with a delay
    // to ensure the boundary geometry is fully committed
    const db = this.viewer.database;
    if (db) {
      const handles = this.params.map((p) => p.handle);
      for (const handle of handles) {
        const entityId = findEntityByHandle(this.module, db, handle);
        if (entityId) {
          HatchReactorManager.getInstance(this.module).syncHatchesForBoundary(
            entityId,
          );
        }
      }
    }
    // Do NOT call viewer.update() here — BaseHandle.end() calls safeViewerUpdate()
    // after history.execute(), so calling it here would trigger a double App.Update()
    // which crashes ODA's render pipeline (null function / null vtable).
  }

  /**
   * Undo the transform (apply inverse).
   */
  undo(): void {
    if (!this.executed) return;
    this.applyTransforms(true);
    this.executed = false;
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  /**
   * Redo the transform (reapply).
   */
  redo(): void {
    if (this.executed) return;
    this.applyTransforms(false);
    this.executed = true;
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  /**
   * Apply transforms to all entities.
   * @param inverse - If true, apply inverse transforms (for undo)
   */
  private applyTransforms(inverse: boolean): void {
    const db = this.viewer.database;
    if (!db) return;

    for (const param of this.params) {
      const entityId = findEntityByHandle(
        this.module,
        this.viewer.database,
        param.handle,
      );
      if (!entityId) continue;

      const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false); // kForWrite = 1
      const entity = this.module.OdDbEntity.cast(pObj);
      if (!entity) {
        deleteObj(pObj);
        continue;
      }

      // Channel order stays "forward" (translation, rotation, scale) even when
      // inverting, which is what this command has always done. See the
      // TransformOrder doc in entityTransform.ts for why that is only sound
      // while a single channel is non-neutral — true for every handle today.
      const appliedAngle = applyTransformToEntity(
        this.module,
        entity,
        param.transform,
        { invert: inverse },
      );

      // Counter-rotate photo marker text to keep it horizontal
      if (appliedAngle !== 0) {
        counterRotatePhotoMarkerText(this.module, entityId, appliedAngle);
      }

      entity.recordGraphicsModified(true);

      // Sync hatches that are reactors on this entity
      HatchReactorManager.getInstance(this.module).syncHatchesForBoundary(
        entityId,
      );

      deleteObj(pObj);
    }
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    // No resources to clean up
  }
}
